import { LinkButton } from '@understory-io/leaf/link-button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { Suspense, useMemo, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Await,
  data,
  Outlet,
  type ShouldRevalidateFunctionArgs,
} from 'react-router';
import { token } from '~/styled-system/tokens';
import {
  BOOKING_DIALOG_SELECTION_PARAMS,
  shouldRevalidateIgnoringBookingDialogParams,
} from '~/routes/checkout/booking/booking-dialog/revalidation';
import { addToWaitlist } from '~/domain/waitlist/index.server';
import type { Route } from './+types/route';
import { Markdown } from '~/ui/markdown';
import { actionError, actionOk, readFormData } from '~/lib/react-router';
import { handleOpenTimeSlotHandoff } from '~/routes/checkout/booking/open/open-handoff.server';
import { storefrontContext } from '~/domain/core/index.server';
import * as tracker from '~/domain/plugins/services/tracker.client';
import { buildViewItemPayload } from '~/domain/plugins/services/tracker/events';
import { useTrackOnce } from '~/domain/plugins/use-track-once';
import {
  trackBarClicked,
  useTrackBookingWidgetViewed,
  widgetModeFor,
} from './tracking';
import { loggerContext } from '~/lib/logger';
import { loadExperiencePage } from './loader.server';
import type { OpenSlotsStatus } from './booking-panel-data.server';
import { Badge } from '@understory-io/leaf/badge';
import { LocationIcon } from '~/ui/icons/location';
import { TimeIcon } from '~/ui/icons/time';
import { LanguageIcon } from '~/ui/icons/language';
import { ArrowUpRightIcon } from '~/ui/icons/arrow-up-right';
import { Gallery } from './components/gallery';
import { HostCard } from '~/ui/host-card';
import { BookingPanelScheduled } from './components/booking-panel-scheduled';
import {
  BookingPanelOpenTimeSlot,
  OPEN_TIME_SLOT_INTENT,
} from './components/booking-panel-open-time-slot';
import { BookingBar } from './components/booking-bar';
import { BookingPanelUnavailable } from './components/booking-panel-unavailable';
import { Itinerary } from './components/itinerary';
import { MediaLightbox } from './components/media-lightbox';
import { buildPageSeo } from '~/lib/seo';
import type { RootLoaderData } from '~/root';
import { safeMailtoHref } from './href';
import { Divider } from '@understory-io/leaf/divider';
import type { RouteHandle } from '~/lib/header-config';
import { localeHref } from '~/lib/use-locale-href';
import { PageCard, TwoColumnLayout } from '~/ui/app-shell/layouts';
import { LocationSection } from './components/location-section';
import { UpcomingDatesSection } from '~/ui/upcoming-dates-section';
import { maxWidthContent } from '~/ui/app-shell/shared';
import { PrivateEventCta } from '~/ui/private-event-cta';
import { cx } from '~/styled-system/css';
import {
  formatDisjunctionList,
  formatDurationOptions,
  formatLocationLabels,
  formatLocationsSummary,
  sortLabels,
} from '~/ui/formatted';

export const handle = {
  header: { backLink: '/' },
} satisfies RouteHandle;

/**
 * Opening the booking dialog (`…/book`) and every in-dialog change (date,
 * tickets, event pick) is a search-param navigation, which would re-run this
 * loader's full fetch chain by default even though it ignores those params.
 * Skip that so the dialog opens/updates without waiting on this page's data.
 * Only the selection params are ignored — this loader DOES read the
 * `language`/`locationId` filter params, so filter changes still revalidate.
 */
export function shouldRevalidate(args: ShouldRevalidateFunctionArgs) {
  return shouldRevalidateIgnoringBookingDialogParams(
    args,
    BOOKING_DIALOG_SELECTION_PARAMS
  );
}

export async function loader({
  params,
  request,
  url,
  context,
}: Route.LoaderArgs) {
  return loadExperiencePage({ params, request, url, context });
}

/**
 * The experience page's two panel-driven posts:
 *
 *  - `join-waitlist` from the BookingWidget's empty-state waitlist component.
 *    Mirrors the checkout route's handler so the same `<Waitlist />` component
 *    can submit from either page without changes.
 *    TODO: when storefronts-core exposes a `features.waitlist.enabled` toggle,
 *    gate this action behind it here and in checkout.
 *  - `book-open` from the open-time-slot widget's "Book now" — commits the chosen
 *    duration + tickets and hands the visitor to the open flow's first step.
 */
export async function action({
  params,
  request,
  url,
  context,
}: Route.ActionArgs) {
  const formData = await readFormData(request);
  const intent = String(formData.get('intent') ?? '');

  if (intent === OPEN_TIME_SLOT_INTENT) {
    return handleOpenTimeSlotHandoff(
      { request, url, params, context },
      formData
    );
  }

  if (intent !== 'join-waitlist') {
    throw data(null, { status: 400 });
  }

  const storefront = context.get(storefrontContext);
  const expId = params.experienceId;
  const email = String(formData.get('email') ?? '').trim();

  if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
    return actionError({ intent: 'join-waitlist', error: 'invalid-email' });
  }

  try {
    const { waitlistId } = await addToWaitlist({
      companyId: storefront.companyId,
      storefrontId: storefront.storefrontId,
      experienceId: expId,
      email,
      languageCode: storefront.resolvedLocale,
      initiatorUrl: url.href,
    });
    return actionOk({ intent: 'join-waitlist', waitlistId });
  } catch (e) {
    const logger = context.get(loggerContext);
    logger.error(e, '[waitlist] Failed to add email to waitlist');
    return actionError(
      { intent: 'join-waitlist', error: 'server-error' },
      { status: 500 }
    );
  }
}

export function meta({ loaderData, matches }: Route.MetaArgs) {
  if (!loaderData) return [];

  const root = matches.find((m) => m?.id === 'root')?.loaderData as
    RootLoaderData | undefined;
  if (!root) return [];

  return buildPageSeo({
    storefront: {
      fqdn: root.fqdn,
      languages: root.languages,
      defaultLanguage: root.defaultLanguage,
      resolvedLocale: root.locale,
      companyName: root.companyName,
      preview: root.preview,
      seoEnabled: root.preferences.seo,
    },
    buildPath: (locale) =>
      localeHref(locale, '/:locale?/experience/:experienceId', {
        experienceId: loaderData.experience.id,
      }),
    pageTitle: loaderData.experience.name,
    // Experience description (markdown-stripped by buildSeo). This is the
    // field legacy named `practicalInfo`; vnext renamed it to `description`.
    // Legacy `infoForGuests` is the field vnext calls `practicalInformation`
    // — that's not what we want here.
    description: loaderData.experience.description,
    // First image is, by data-model convention, the default. The legacy
    // app's `media.primaryImage.url` resolves to the same picture.
    ogImage:
      loaderData.experience.media.find((m) => m.type === 'image')?.url ?? null,
  });
}

export default function ExperienceDetails({
  loaderData,
  params,
}: Route.ComponentProps) {
  const {
    experience,
    host,
    hostName,
    checkoutHref,
    privateEventRequestEnabled,
    defaultPrice,
  } = loaderData;
  const intl = useIntl();

  // Detail page viewed — the funnel's `view_item` moment (US-11215). Skipped
  // when no default price resolves; the container's trigger expects a value.
  useTrackOnce(() => {
    if (!defaultPrice) return;
    tracker.event(
      'view_item',
      buildViewItemPayload({
        experienceId: experience.id,
        experienceName: experience.name,
        currency: defaultPrice.currency,
        priceCents: defaultPrice.amountCents,
      })
    );
  }, [experience.id]);
  useTrackBookingWidgetViewed({
    experienceId: experience.id,
    availabilityMode: loaderData.availabilityMode,
    bookingWidgetMode: loaderData.bookingWidgetMode,
    events: loaderData.events,
    canProceedToCheckout: loaderData.canProceedToCheckout,
  });
  const summaryLabel = intl.formatMessage({
    defaultMessage: 'Experience summary',
    description: 'Accessible name for the pill list of experience attributes',
  });
  const mailtoHref = host?.email ? safeMailtoHref(host.email) : null;

  const unavailablePanel = (
    <BookingPanelUnavailable
      canProceedToCheckout={loaderData.canProceedToCheckout}
    />
  );
  // Nothing to book: the same waitlist a fixed-events experience without dates
  // offers, so both kinds of experience say "no dates" the same way.
  const noTimesPanel = (
    <BookingPanelScheduled
      experienceId={experience.id}
      checkoutHref={checkoutHref}
      events={[]}
      mode="empty"
      defaultPrice={loaderData.defaultPrice}
      showFromPrice={loaderData.showFromPrice}
      privateEventRequestEnabled={privateEventRequestEnabled}
      locale={params.locale}
      canProceedToCheckout={loaderData.canProceedToCheckout}
    />
  );
  // Mobile-only floating CTA — the simpler counterpart to the tablet+ booking
  // panel (which is display:none on mobile), so this is the only mobile booking
  // affordance. It links to the full-screen checkout page, which shows the
  // availability calendar or, with no dates, the waitlist (US-10809). The empty
  // variant shows regardless of checkout access since joining a waitlist needs
  // no payment; bookable modes still require checkout to be reachable.
  function bookingBar(isEmpty: boolean): ReactNode {
    if (!isEmpty && !loaderData.canProceedToCheckout) return null;
    return (
      <div className={mobileOnly}>
        <BookingBar
          checkoutHref={checkoutHref}
          isEmpty={isEmpty}
          defaultPrice={loaderData.defaultPrice}
          showFromPrice={loaderData.showFromPrice}
          onClick={() =>
            trackBarClicked({
              experienceId: experience.id,
              widgetMode: widgetModeFor(
                loaderData.availabilityMode,
                loaderData.bookingWidgetMode
              ),
              isEmpty,
            })
          }
        />
      </div>
    );
  }
  const openPanel = loaderData.openTimeSlot && (
    <BookingPanelOpenTimeSlot
      experienceId={experience.id}
      defaultPrice={loaderData.defaultPrice}
      showFromPrice={loaderData.showFromPrice}
      durationsSeconds={loaderData.openTimeSlot.durationsSeconds}
      variants={loaderData.openTimeSlot.variants}
      seatStep={loaderData.openTimeSlot.seatStep}
      canProceedToCheckout={loaderData.canProceedToCheckout}
    />
  );
  function openSlotsPanel(openSlots: OpenSlotsStatus): ReactNode {
    switch (openSlots) {
      case 'unavailable':
        return unavailablePanel;
      case 'none':
        return noTimesPanel;
      case 'bookable':
        return openPanel;
    }
  }

  // One location chip per place the experience runs. `formatLocationLabels`
  // dedupes on the rendered label (city when upstream knows it, otherwise the
  // formatted address) so several addresses in the same city collapse into a
  // single chip — the map section still plots every individual address.
  const locationLabels = useMemo(
    () => formatLocationLabels(experience.locations),
    [experience.locations]
  );

  // One label covering every offered duration ("1 or 2 hours", "4–8 hours");
  // null hides the badge entirely.
  const durationLabel = formatDurationOptions(
    intl,
    experience.durationsMinutes
  );

  // The gallery lightbox is desktop-only — on mobile the gallery already
  // exposes every tile via horizontal scroll, so there's nothing to "expand"
  // to. `null` keeps the lightbox closed; a number is the index of the tile
  // that was clicked.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const handleMediaClick = (index: number) => {
    if (typeof window === 'undefined') return;
    const isDesktop = window.matchMedia(
      `(min-width: ${token('breakpoints.tablet')})`
    ).matches;
    if (!isDesktop) return;
    setLightboxIndex(index);
  };

  return (
    <>
      {/* Booking dialog (desktop modal route) renders here when open. */}
      <Outlet />
      <PageCard>
        <Stack gap={{ base: 'large', tablet: '3xlarge' }}>
          {experience.media.length > 0 ? (
            <>
              <Gallery
                media={experience.media}
                onMediaClick={handleMediaClick}
              />
              <MediaLightbox
                media={experience.media}
                openAtIndex={lightboxIndex}
                onClose={() => setLightboxIndex(null)}
              />
            </>
          ) : (
            <span></span>
          )}

          <Stack gap="3xlarge">
            <TwoColumnLayout>
              <Stack gap="xlarge">
                <Stack gap="xlarge">
                  <Stack gap="xsmall">
                    <Stack gap="micro">
                      <Text as="h1" style="headline">
                        {experience.name}
                      </Text>
                      {hostName && (
                        <Text
                          as="span"
                          style="body-small-regular"
                          color="tertiary"
                        >
                          <FormattedMessage
                            defaultMessage="by {hostName}"
                            description="Experience host name shown under the title on the experience details page for a shared Connect experience"
                            values={{ hostName }}
                          />
                        </Text>
                      )}
                    </Stack>
                    <Stack
                      as="ul"
                      direction="row"
                      gap="small"
                      wrap
                      aria-label={summaryLabel}
                      className={pillsListStyles}
                    >
                      {locationLabels.length > 0 && (
                        <li>
                          <Badge
                            appearance="neutral"
                            size="large"
                            icon={
                              <span className={badgeIconStyles}>
                                <LocationIcon />
                              </span>
                            }
                          >
                            {/* Badge accepts only a string child, so format
                                via the helpers rather than components. */}
                            {formatLocationsSummary(intl, locationLabels)}
                          </Badge>
                        </li>
                      )}
                      {durationLabel !== null && (
                        <li>
                          <Badge
                            appearance="neutral"
                            size="large"
                            icon={
                              <span className={badgeIconStyles}>
                                <TimeIcon />
                              </span>
                            }
                          >
                            {durationLabel}
                          </Badge>
                        </li>
                      )}
                      {experience.languages.length > 0 && (
                        <li>
                          <Badge
                            appearance="neutral"
                            size="large"
                            icon={
                              <span className={badgeIconStyles}>
                                <LanguageIcon />
                              </span>
                            }
                          >
                            {formatDisjunctionList(
                              intl,
                              sortLabels(
                                intl.locale,
                                experience.languages.map((l) => l.label)
                              )
                            )}
                          </Badge>
                        </li>
                      )}
                    </Stack>
                  </Stack>
                  {experience.description && (
                    <Markdown>{experience.description}</Markdown>
                  )}
                  {experience.whatsIncluded && (
                    <Stack as="section" gap="small">
                      <Text as="h2" style="subheadline-small">
                        <FormattedMessage
                          defaultMessage="What's included"
                          description="Section heading on the experience details page listing what's included with the experience"
                        />
                      </Text>
                      <Markdown options={{ useCheckmarksForLists: true }}>
                        {experience.whatsIncluded}
                      </Markdown>
                    </Stack>
                  )}
                </Stack>
                {experience.itinerary && (
                  <Itinerary
                    title={experience.itinerary.title}
                    items={experience.itinerary.items}
                  />
                )}
                {experience.practicalInformation && (
                  <Stack
                    as="section"
                    gap="medium"
                    className={practicalInformationStyles}
                  >
                    <Text as="h2" style="subheadline-small">
                      <FormattedMessage
                        defaultMessage="Practical information"
                        description="Section heading on the experience details page for practical information for guests"
                      />
                    </Text>
                    <Markdown>{experience.practicalInformation}</Markdown>
                  </Stack>
                )}
              </Stack>

              {/* Tablet+ only — the mobile CTA is the floating <BookingBar>
                  below. `display: contents` keeps the card a direct child of
                  TwoColumnLayout's stretched <aside> so its sticky positioning
                  still works.

                  How the experience defines its availability picks the panel:
                  an open-timeslot (operating hours) experience has no events to
                  choose from, so instead of a date/timeslot picker it offers
                  duration + tickets and hands that to the checkout's open flow,
                  which asks where and when. The two panels share only the card
                  they sit in, so the choice is made here rather than inside
                  one of them. */}
              <div className={tabletUpOnly}>
                {loaderData.availabilityUnavailable ? (
                  unavailablePanel
                ) : loaderData.openTimeSlot ? (
                  <AwaitOpenSlots openSlots={loaderData.openTimeSlot.openSlots}>
                    {openSlotsPanel}
                  </AwaitOpenSlots>
                ) : (
                  <BookingPanelScheduled
                    experienceId={experience.id}
                    checkoutHref={checkoutHref}
                    events={loaderData.events}
                    mode={loaderData.bookingWidgetMode}
                    initialFocusDate={loaderData.bookingWidgetInitialFocusDate}
                    filterOptions={loaderData.filterOptions}
                    seatCounterVisibility={loaderData.seatCounterVisibility}
                    defaultPrice={loaderData.defaultPrice}
                    showFromPrice={loaderData.showFromPrice}
                    privateEventRequestEnabled={privateEventRequestEnabled}
                    locale={params.locale}
                    canProceedToCheckout={loaderData.canProceedToCheckout}
                  />
                )}
              </div>
            </TwoColumnLayout>

            <LocationSection locations={experience.locations} />

            {loaderData.events.length > 0 && (
              <UpcomingDatesSection
                // Slice client-side so the loader keeps fetching enough
                // events to drive the BookingWidget's mode decision; only
                // the page-level "Upcoming dates" section is capped.
                events={loaderData.events.slice(0, 3)}
                seatCounterVisibility={loaderData.seatCounterVisibility}
                seeAllHref={checkoutHref}
              />
            )}

            {host && (
              <HostCard
                header={host.name}
                description={host.hostDescription}
                coverPhoto={host.coverPhoto}
              >
                {mailtoHref && (
                  <LinkButton
                    href={mailtoHref}
                    appearance="secondary"
                    size="large"
                  >
                    <FormattedMessage
                      defaultMessage="Contact us"
                      description="Action on the host card that opens a mailto link to the host"
                    />
                    <ArrowUpRightIcon size="small" />
                  </LinkButton>
                )}
              </HostCard>
            )}
          </Stack>
        </Stack>
      </PageCard>

      {privateEventRequestEnabled && (
        <>
          <PrivateEventCta
            locale={params.locale}
            heading={
              <FormattedMessage
                defaultMessage="Are you looking for a custom event?"
                description="Heading of the custom event request block on the experience details page"
              />
            }
            body={
              <FormattedMessage
                defaultMessage="We can arrange custom events for larger groups, such as companies or friend groups. Leave us your details and we'll get back to you as soon as possible."
                description="Body copy of the custom event request block on the experience details page"
              />
            }
            ctaLabel={
              <FormattedMessage
                defaultMessage="Request event"
                description="Action on the custom event block that opens the private event request form"
              />
            }
          />

          <div
            className={cx(
              maxWidthContent,
              css({
                px: { base: 'small', tablet: 'large' },
              })
            )}
          >
            <Divider />
          </div>
        </>
      )}

      {loaderData.openTimeSlot ? (
        // Same streamed answer as the panel: no bookable times turns the bar
        // into its waitlist variant, and the open checkout's date step offers
        // the waitlist it leads to.
        <AwaitOpenSlots openSlots={loaderData.openTimeSlot.openSlots}>
          {(openSlots) => bookingBar(openSlots === 'none')}
        </AwaitOpenSlots>
      ) : (
        bookingBar(loaderData.bookingWidgetMode === 'empty')
      )}
    </>
  );
}

type AwaitOpenSlotsProps = {
  openSlots: Promise<OpenSlotsStatus>;
  children: (openSlots: OpenSlotsStatus) => ReactNode;
};

/**
 * Renders as bookable until the open slots answer, and if they never do: an
 * outage must not stop sales, and the checkout's open flow checks again.
 */
function AwaitOpenSlots({
  openSlots,
  children,
}: AwaitOpenSlotsProps): ReactNode {
  const untilAnswered = children('bookable');
  return (
    <Suspense fallback={untilAnswered}>
      <Await resolve={openSlots} errorElement={untilAnswered}>
        {children}
      </Await>
    </Suspense>
  );
}

// Viewport gates for the two booking CTAs, via Panda's hideFrom / hideBelow
// (each just sets `display: none` inside a breakpoint media query). The panel
// also keeps `display: contents` for its shown state so the wrapper adds no
// box — the card stays a direct child of TwoColumnLayout's stretched <aside>,
// preserving its sticky positioning. The bar is `position: fixed`, so its
// wrapper's default (block) shown state is harmless.
const tabletUpOnly = css({ display: 'contents', hideBelow: 'tablet' });
const mobileOnly = css({ hideFrom: 'tablet' });

const pillsListStyles = css({
  listStyle: 'none',
  margin: 'none',
  padding: 'none',
});

// The intro badges render their icon in the secondary content colour while the
// label text stays primary. Leaf's Badge doesn't colour the icon itself, so we
// set the colour on the icon we pass in (the icons use `currentColor`).
const badgeIconStyles = css({
  color: 'content.secondary',
  display: 'inline-flex',
});

// Leaf's radii scale jumps from `large` (20px) to `xlarge` (32px), so 24px is
// hand-coded here. Adjust to a token if the design system grows one.
const practicalInformationStyles = css({
  border: 'small',
  borderColor: 'border.neutral',
  borderRadius: '[24px]',
  padding: 'large',
  background: 'surface.raised',
});
